import React, { useState } from 'react';
import { Shield, Lock, Eye, Database, UserCheck, FileText, Download, Trash2, Mail, AlertCircle, CheckCircle, X, ChevronDown, ChevronUp } from 'lucide-react';

const GDPRCompliancePage = ({ onClose }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const AnimatedBackground = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-emerald-950" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" 
           style={{ animation: 'float 8s ease-in-out infinite' }} />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" 
           style={{ animation: 'float 10s ease-in-out infinite reverse' }} />
      <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse" 
           style={{ animation: 'float 12s ease-in-out infinite' }} />
    </div>
  );

  const gdprSections = [
    {
      id: 'overview',
      icon: Shield,
      title: 'GDPR Overview',
      gradient: 'from-blue-500 to-cyan-600',
      content: `The General Data Protection Regulation (GDPR) is a comprehensive data protection law that came into effect on May 25, 2018. At FreeIeee, we are fully committed to GDPR compliance and protecting your personal data rights.

We process personal data lawfully, fairly, and transparently. This page outlines our commitment to GDPR principles and explains your rights as a data subject under this regulation.`
    },
    {
      id: 'data-collection',
      icon: Database,
      title: 'What Data We Collect',
      gradient: 'from-emerald-500 to-teal-600',
      content: `We collect and process the following types of personal data:

**Account Information:**
• Email address (required for account creation)
• Name (optional, for personalization)
• Password (encrypted and securely stored)

**Usage Data:**
• Papers and documents you create
• Service usage analytics and statistics
• IP address and device information
• Browser type and operating system

**Payment Information:**
• Billing details (processed by secure payment providers)
• Transaction history
• Subscription status

We only collect data that is necessary for providing and improving our service.`
    },
    {
      id: 'legal-basis',
      icon: FileText,
      title: 'Legal Basis for Processing',
      gradient: 'from-sky-500 to-blue-600',
      content: `We process your personal data based on the following legal grounds:

**Contract Performance:** Processing necessary to provide our IEEE paper generation service that you've signed up for.

**Legitimate Interest:** Improving our service, preventing fraud, and ensuring security.

**Consent:** Marketing communications and optional features (you can withdraw consent at any time).

**Legal Obligation:** Compliance with applicable laws and regulations.`
    },
    {
      id: 'your-rights',
      icon: UserCheck,
      title: 'Your GDPR Rights',
      gradient: 'from-green-500 to-emerald-600',
      content: `Under GDPR, you have the following rights:

**Right to Access:** Request copies of your personal data we hold.

**Right to Rectification:** Request correction of inaccurate or incomplete data.

**Right to Erasure ("Right to be Forgotten"):** Request deletion of your personal data.

**Right to Restrict Processing:** Request limitation of how we use your data.

**Right to Data Portability:** Receive your data in a structured, commonly used format.

**Right to Object:** Object to processing based on legitimate interests.

**Right to Withdraw Consent:** Withdraw consent for processing at any time.

**Right to Lodge a Complaint:** File a complaint with your local data protection authority.

To exercise any of these rights, please contact us at gdpr@freeieee.com`
    },
    {
      id: 'data-security',
      icon: Lock,
      title: 'Data Security Measures',
      gradient: 'from-teal-500 to-cyan-600',
      content: `We implement robust security measures to protect your data:

**Encryption:**
• All data transmitted over HTTPS/TLS encryption
• Passwords encrypted using industry-standard algorithms
• Database encryption at rest

**Access Controls:**
• Role-based access control systems
• Multi-factor authentication for administrative access
• Regular access audits and reviews

**Technical Safeguards:**
• Regular security vulnerability assessments
• Automated threat detection systems
• Secure backup and disaster recovery procedures
• Regular software updates and patches

**Organizational Measures:**
• Employee data protection training
• Strict confidentiality agreements
• Data protection impact assessments
• Incident response procedures`
    },
    {
      id: 'data-retention',
      icon: Eye,
      title: 'Data Retention',
      gradient: 'from-indigo-500 to-blue-600',
      content: `We retain personal data only for as long as necessary:

**Active Accounts:** Data retained while your account is active and for a reasonable period after account closure.

**Billing Records:** Retained for 7 years to comply with tax and accounting regulations.

**Analytics Data:** Anonymized after 26 months.

**Marketing Data:** Deleted upon unsubscribe or withdrawal of consent.

**Legal Requirements:** Some data may be retained longer if required by law or for legal proceedings.

You can request deletion of your data at any time by contacting us.`
    },
    {
      id: 'third-party',
      icon: AlertCircle,
      title: 'Third-Party Sharing',
      gradient: 'from-purple-500 to-indigo-600',
      content: `We share personal data only when necessary:

**Service Providers:**
• Cloud hosting providers (AWS, Google Cloud)
• Payment processors (Stripe, PayPal)
• Email service providers
• Analytics providers

All third-party providers are GDPR-compliant and bound by data processing agreements.

**Legal Requirements:**
We may disclose data when required by law, court order, or to protect our rights and safety.

**Business Transfers:**
In case of merger or acquisition, your data may be transferred to the new entity under the same privacy commitments.

We never sell your personal data to third parties.`
    },
    {
      id: 'international',
      icon: Database,
      title: 'International Data Transfers',
      gradient: 'from-pink-500 to-rose-600',
      content: `FreeIeee operates globally and may transfer data internationally:

**Data Location:**
• Primary servers located in the EU
• Backup servers in GDPR-compliant regions
• CDN services for improved performance

**Transfer Safeguards:**
• Standard Contractual Clauses (SCCs) approved by EU Commission
• Privacy Shield certification (where applicable)
• Adequacy decisions for transfers to approved countries

We ensure all international transfers comply with GDPR requirements and provide adequate protection for your data.`
    },
    {
      id: 'contact',
      icon: Mail,
      title: 'Contact & Data Protection Officer',
      gradient: 'from-orange-500 to-red-600',
      content: `For any GDPR-related questions or to exercise your rights:

**Data Protection Officer:**
Email: dpo@ieeeassistant.in


**GDPR Requests:**
Email: gdpr@ieeeassistant.com
Response Time: Within 30 days

**General Support:**
Email: support@ieeeassistant.com

**Mailing Address:**
ieeeassistant / scopewit sp.zo.o. technologies pvt. ltd.
77 Hudkeshwar road
Nagpur, India

**Supervisory Authority:**
You have the right to lodge a complaint with your local data protection authority if you believe your rights have been violated.`
    }
  ];

  return (
    <div className="relative min-h-screen bg-black overflow-hidden">
      <AnimatedBackground />

      {/* Header */}
      <div className="relative z-10">
        <nav className="bg-black/80 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center gap-3">
                <Shield className="w-10 h-10 text-blue-400" />
                <span className="text-white font-bold text-2xl">GDPR Compliance</span>
              </div>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-white/60 hover:text-white transition"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-6 py-3 mb-8">
              <Shield className="w-5 h-5 text-emerald-400" />
              <span className="text-white text-sm font-medium">Your Privacy, Our Priority</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              GDPR Compliance
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              At FreeIeee, we're committed to protecting your personal data and respecting your privacy rights 
              under the General Data Protection Regulation (GDPR).
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-4 gap-4 mb-16">
            {[
              { icon: Download, label: 'Request Your Data', color: 'blue' },
              { icon: Trash2, label: 'Delete Account', color: 'red' },
              { icon: FileText, label: 'View Privacy Policy', color: 'emerald' },
              { icon: Mail, label: 'Contact DPO', color: 'teal' }
            ].map((action, idx) => (
              <button
                key={idx}
                className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all transform hover:scale-105 group`}
              >
                <action.icon className={`w-8 h-8 text-${action.color}-400 mx-auto mb-3 group-hover:scale-110 transition`} />
                <div className="text-white font-medium text-sm">{action.label}</div>
              </button>
            ))}
          </div>

          {/* Main Content Sections */}
          <div className="space-y-6">
            {gdprSections.map((section, idx) => (
              <div
                key={section.id}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full p-6 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className={`bg-gradient-to-br ${section.gradient} w-12 h-12 rounded-xl flex items-center justify-center`}>
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white text-left">{section.title}</h2>
                  </div>
                  {expandedSection === section.id ? (
                    <ChevronUp className="w-6 h-6 text-white/60" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-white/60" />
                  )}
                </button>

                {expandedSection === section.id && (
                  <div className="px-6 pb-6">
                    <div className="bg-black/20 rounded-xl p-6 border border-white/5">
                      <div className="text-white/80 leading-relaxed whitespace-pre-line">
                        {section.content}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Certification Badges */}
          <div className="mt-16 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white text-center mb-8">Our Certifications & Compliance</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: Shield, label: 'GDPR Compliant', desc: 'Full compliance since 2018' },
                { icon: Lock, label: 'ISO 27001', desc: 'Information security' },
                { icon: CheckCircle, label: 'SOC 2 Type II', desc: 'Certified secure' },
                { icon: Database, label: 'Data Protection', desc: 'EU standards' }
              ].map((cert, idx) => (
                <div key={idx} className="text-center">
                  <div className="bg-gradient-to-br from-blue-500/20 to-emerald-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 border border-blue-500/30">
                    <cert.icon className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="text-white font-bold mb-1">{cert.label}</div>
                  <div className="text-white/60 text-sm">{cert.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-8 text-center">
            <p className="text-white/60 text-sm">
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="text-white/40 text-xs mt-2">
              Version 2.0 - Effective from May 25, 2018
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
      `}</style>
    </div>
  );
};

export default GDPRCompliancePage;